import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { withRouter, Route, Switch, useHistory, useParams, useLocation } from 'react-router-dom';
import { Button, Icon, Collapse } from '@material-ui/core';
import { AuthContext } from '../../../context/authContext';
import ACCESS from '../../../ACCESS';
import { ClienteService } from '../../../services/ClienteService';
import ClienteForm from './ClienteForm';

// Importación de las 17 subsecciones existentes
import DatosGenerales from './datosgenerales/DatosGenerales';
import Grupos from './grupoClientes/grupoClientes';
import Remitentes from './remitentes/Remitentes';
import Destinatarios from './destinatarios/Destinatarios';
import Ventas from './ventas/Ventas';
import Administrativos from './administrativos/Administrativos';
import Estadisticos from './estadisticos/Estadistico';
import Representante from './repLegal/RepLegal';
import Cfdi from './cfdi/Cfdi';
import Convenios from './convenios/Convenios';
import Precios from './precios/Precios';
import Pagos from './pagos/Pagos';
import FoliosUso from './folios-uso/FoliosUso';
import PagosSucursales from './pagoTransportista/PagosSucursales';
import Factoraje from './factoraje/Factoraje';
import ApiConfig from './api/apiConfig';
import EdiConfig from './edi/EdiConfig';

// Definición de las 4 categorías lógicas para organizar las 17 subsecciones
const CATEGORIES = [
  {
    id: 'general',
    name: 'General y Fiscal',
    description: 'Datos base, facturación CFDI y representante',
    icon: 'description',
    color: 'blue',
  },
  {
    id: 'operacion',
    name: 'Operación y Envíos',
    description: 'Remitentes, destinatarios y fletes',
    icon: 'local_shipping',
    color: 'emerald',
  },
  {
    id: 'comercial',
    name: 'Comercial y Cobranza',
    description: 'Ventas, precios, convenios y condiciones de pago',
    icon: 'monetization_on',
    color: 'amber',
  },
  {
    id: 'integraciones',
    name: 'Integraciones',
    description: 'Conexiones vía API y módulos EDI',
    icon: 'settings_ethernet',
    color: 'indigo',
  },
];

// Configuración completa de las 17 subsecciones agrupadas por categoría
const ALL_SUBSECTIONS = [
  // --- Categoría: General y Fiscal ---
  {
    id: 'Generales',
    label: 'Datos Generales',
    shortLabel: 'Generales',
    category: 'general',
    icon: 'assignment',
    route: 'Generales',
    path: '/Catalogo/Cliente/:id/Generales',
    Component: DatosGenerales,
    access: [
      ACCESS.CATALOGO_CLIENTES.VER_CATALOGO,
      ACCESS.CATALOGO_CLIENTES.VER_SOLO_CONTADO,
      ACCESS.CATALOGO_CLIENTES.VER_SOLO_CREDITO,
    ],
  },
  {
    id: 'Cfdi',
    label: 'Facturación CFDI',
    shortLabel: 'CFDI',
    category: 'general',
    icon: 'receipt',
    route: 'Cfdi',
    path: '/Catalogo/Cliente/:id/Cfdi/:tabId?',
    Component: Cfdi,
    access: [ACCESS.CATALOGO_CLIENTES_CFDI.VER_CATALOGO],
  },
  {
    id: 'Representante',
    label: 'Representante Legal',
    shortLabel: 'Rep. Legal',
    category: 'general',
    icon: 'gavel',
    route: 'Representante',
    path: '/Catalogo/Cliente/:id/Representante/:tabId?',
    Component: Representante,
    access: [ACCESS.CATALOGO_CLIENTES_REP_LEGAL.VER_CATALOGO],
  },
  {
    id: 'Estadisticos',
    label: 'Datos Estadísticos',
    shortLabel: 'Estadístico',
    category: 'general',
    icon: 'bar_chart',
    route: 'Estadisticos',
    path: '/Catalogo/Cliente/:id/Estadisticos/:tabId?',
    Component: Estadisticos,
    access: [ACCESS.CATALOGO_CLIENTES_ESTADISTICO.VER_CATALOGO],
  },

  // --- Categoría: Operación y Envíos ---
  {
    id: 'Remitentes',
    label: 'Remitentes',
    shortLabel: 'Remitentes',
    category: 'operacion',
    icon: 'arrow_upward',
    route: 'Remitentes',
    path: '/Catalogo/Cliente/:id/Remitentes/:tabId?',
    Component: Remitentes,
    access: [ACCESS.CATALOGO_CLIENTES_REMITENTE.VER_CATALOGO],
  },
  {
    id: 'Destinatarios',
    label: 'Destinatarios',
    shortLabel: 'Destinatarios',
    category: 'operacion',
    icon: 'arrow_downward',
    route: 'Destinatarios',
    path: '/Catalogo/Cliente/:id/Destinatarios/:tabId?',
    Component: Destinatarios,
    access: [ACCESS.CATALOGO_CLIENTES_DESTINATARIO.VER_CATALOGO],
  },
  {
    id: 'PagoTransportista',
    label: 'Pago a Transportista',
    shortLabel: 'Pago Transp.',
    category: 'operacion',
    icon: 'commute',
    route: 'PagoTransportista',
    path: '/Catalogo/Cliente/:id/PagoTransportista/:tabId?',
    Component: PagosSucursales,
    access: [ACCESS.CATALOGO_CLIENTES_PAGO_TRANSPORTISTA.VER_CATALOGO],
  },
  {
    id: 'FoliosUso',
    label: 'Folios de Uso',
    shortLabel: 'Folios',
    category: 'operacion',
    icon: 'confirmation_number',
    route: 'FoliosUso',
    path: '/Catalogo/Cliente/:id/FoliosUso/:tabId?',
    Component: FoliosUso,
    access: [ACCESS.CATALOGO_CLIENTES_FOLIO.EDITAR_CATALOGO],
  },

  // --- Categoría: Comercial y Cobranza ---
  {
    id: 'Ventas',
    label: 'Condiciones de Venta',
    shortLabel: 'Ventas',
    category: 'comercial',
    icon: 'trending_up',
    route: 'Ventas',
    path: '/Catalogo/Cliente/:id/Ventas/:tabId?',
    Component: Ventas,
    access: [ACCESS.CATALOGO_CLIENTES_VENTA.EDITAR_CATALOGO],
  },
  {
    id: 'Precios',
    label: 'Listas de Precios',
    shortLabel: 'Precios',
    category: 'comercial',
    icon: 'sell',
    route: 'Precios',
    path: '/Catalogo/Cliente/:id/Precios/:tabId?',
    Component: Precios,
    access: [ACCESS.CATALOGO_CLIENTES_PRECIO.VER_CATALOGO],
  },
  {
    id: 'Convenios',
    label: 'Convenios',
    shortLabel: 'Convenios',
    category: 'comercial',
    icon: 'handshake',
    route: 'Convenios',
    path: '/Catalogo/Cliente/:id/Convenios/:tabId?',
    Component: Convenios,
    access: [ACCESS.CATALOGO_CLIENTES_CONVENIO.VER_CATALOGO],
  },
  {
    id: 'Pagos',
    label: 'Condiciones de Pago',
    shortLabel: 'Pagos',
    category: 'comercial',
    icon: 'payments',
    route: 'Pagos',
    path: '/Catalogo/Cliente/:id/Pagos/:tabId?',
    Component: Pagos,
    access: [ACCESS.CATALOGO_CLIENTES_PAGO.VER_CATALOGO],
  },
  {
    id: 'Administrativos',
    label: 'Datos Administrativos',
    shortLabel: 'Administrativo',
    category: 'comercial',
    icon: 'account_balance',
    route: 'Administrativos',
    path: '/Catalogo/Cliente/:id/Administrativos/:tabId?',
    Component: Administrativos,
    access: [ACCESS.CATALOGO_CLIENTES_ADMINISTRATIVO.VER_CATALOGO],
  },
  {
    id: 'Factoraje',
    label: 'Factoraje Financiero',
    shortLabel: 'Factoraje',
    category: 'comercial',
    icon: 'account_balance_wallet',
    route: 'Factoraje',
    path: '/Catalogo/Cliente/:id/Factoraje/:tabId?',
    Component: Factoraje,
    access: [ACCESS.CATALOGO_CLIENTES_FACTORAJE.VER_CATALOGO],
  },
  {
    id: 'Grupos',
    label: 'Grupo de Clientes',
    shortLabel: 'Grupos',
    category: 'comercial',
    icon: 'groups',
    route: 'Grupos',
    path: '/Catalogo/Cliente/:id/Grupos/:tabId?',
    Component: Grupos,
    access: [ACCESS.CATALOGO_CLIENTES_GRUPO.VER_CATALOGO],
  },

  // --- Categoría: Integraciones ---
  {
    id: 'Api',
    label: 'Configuración API',
    shortLabel: 'API',
    category: 'integraciones',
    icon: 'api',
    route: 'Api',
    path: '/Catalogo/Cliente/:id/API/:tabId?',
    Component: ApiConfig,
    access: [ACCESS.CATALOGO_CLIENTES_API.VER_CATALOGO],
  },
  {
    id: 'Edi',
    label: 'Integración EDI',
    shortLabel: 'EDI',
    category: 'integraciones',
    icon: 'sync_alt',
    route: 'Edi',
    path: '/Catalogo/Cliente/:id/Edi/:tabId?',
    Component: EdiConfig,
    access: [ACCESS.CATALOGO_CLIENTES.VER_CATALOGO],
  },
];

export const ClienteInfo = () => {
  const { id } = useParams() || {};
  const auth = useContext(AuthContext);

  const hasAccess = useCallback(
    (permission) => (auth && auth.hasAccess ? auth.hasAccess(permission) : true),
    [auth]
  );

  // Filtrar subsecciones según permisos del usuario
  const accessibleSubsections = useMemo(() => {
    return ALL_SUBSECTIONS.filter((sub) => {
      if (!sub.access || sub.access.length === 0) return true;
      return hasAccess(sub.access);
    });
  }, [hasAccess]);

  // Si no hay ID, estamos en modo "Nuevo Cliente"
  if (!id) {
    return (
      <div className="w-full p-4 md:p-6 max-w-6xl mx-auto">
        <ClienteForm backUrl="/Catalogo/Clientes" />
      </div>
    );
  }

  return <ClienteEditor id={id} accessibleSubsections={accessibleSubsections} />;
};

const ClienteEditor = ({ id, accessibleSubsections }) => {
  const history = useHistory();
  const location = useLocation();
  const baseUrl = `/Catalogo/Cliente/${id}`;

  const [clienteData, setClienteData] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(true);
  const [showEditMaster, setShowEditMaster] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Cargar datos principales del cliente para la cabecera
  const fetchCliente = useCallback(async () => {
    setLoadingCliente(true);
    try {
      const data = await ClienteService.obtenerDetalleCompleto(id);
      setClienteData(data);
    } catch (e) {
      console.error('Error cargando cliente:', e);
    } finally {
      setLoadingCliente(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCliente();
  }, [fetchCliente]);

  // Detectar subsección activa a partir de la URL actual
  const currentSubsection = useMemo(() => {
    const path = location.pathname;
    const match = accessibleSubsections.find((sub) => {
      return (
        path === `${baseUrl}/${sub.route}` ||
        path.startsWith(`${baseUrl}/${sub.route}/`) ||
        (sub.route === 'Api' && (path === `${baseUrl}/API` || path.startsWith(`${baseUrl}/API/`)))
      );
    });
    return match || accessibleSubsections[0] || null;
  }, [location.pathname, baseUrl, accessibleSubsections]);

  // Categoría seleccionada por el usuario (o por la ruta activa)
  const [activeCategory, setActiveCategory] = useState(
    currentSubsection ? currentSubsection.category : 'general'
  );

  // Sincronizar categoría cuando cambie la subsección activa por navegación
  useEffect(() => {
    if (currentSubsection && currentSubsection.category !== activeCategory) {
      setActiveCategory(currentSubsection.category);
    }
  }, [currentSubsection, activeCategory]);

  // Redirigir a la primera subsección si está en la raíz de /Catalogo/Cliente/:id
  useEffect(() => {
    if (location.pathname === baseUrl && accessibleSubsections.length > 0) {
      history.replace(`${baseUrl}/${accessibleSubsections[0].route}`);
    }
  }, [location.pathname, baseUrl, accessibleSubsections, history]);

  // Filtrar categorías disponibles (solo las que tienen al menos una subsección permitida)
  const availableCategories = useMemo(() => {
    return CATEGORIES.filter((cat) =>
      accessibleSubsections.some((sub) => sub.category === cat.id)
    );
  }, [accessibleSubsections]);

  // Subsecciones pertenecientes a la categoría activa
  const categorySubsections = useMemo(() => {
    return accessibleSubsections.filter((sub) => sub.category === activeCategory);
  }, [accessibleSubsections, activeCategory]);

  // Subsecciones filtradas por término de búsqueda (búsqueda rápida)
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase().trim();
    return accessibleSubsections.filter(
      (sub) =>
        sub.label.toLowerCase().includes(term) ||
        sub.shortLabel.toLowerCase().includes(term) ||
        sub.route.toLowerCase().includes(term)
    );
  }, [accessibleSubsections, searchTerm]);

  const handleSelectSubsection = (sub) => {
    setActiveCategory(sub.category);
    setSearchTerm('');
    history.push(`${baseUrl}/${sub.route}`);
  };

  return (
    <div className="w-full p-3 md:p-4 max-w-7xl mx-auto space-y-3">
      {/* 1. CABECERA PRINCIPAL DEL CLIENTE */}
      <div className="bg-white rounded-xl shadow-2xs border border-gray-200/80 px-4 py-2.5 md:px-5 md:py-3 transition-all duration-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => history.push('/Catalogo/Clientes')}
              className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              title="Volver a lista de Clientes"
            >
              <Icon fontSize="small">arrow_back</Icon>
            </button>
            <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 font-bold shadow-2xs">
              <Icon fontSize="small">business</Icon>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-base md:text-lg font-bold text-gray-900 tracking-tight leading-tight">
                  {loadingCliente ? (
                    <span className="text-gray-400">Cargando cliente...</span>
                  ) : (
                    clienteData?.cliente_rsocial || `Cliente #${clienteData?.cliente || id}`
                  )}
                </h1>
                {clienteData && (
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                    Clave: {clienteData.cliente || id}
                  </span>
                )}
                {clienteData?.cliente_fm && (
                  <span
                    className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                      Number(clienteData.cliente_fm) === 1
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                        : 'bg-purple-100 text-purple-800 border border-purple-200'
                    }`}
                  >
                    {Number(clienteData.cliente_fm) === 1 ? 'Persona Física' : 'Persona Moral'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5 flex-wrap">
                <span>
                  <strong>RFC:</strong> {clienteData?.cliente_rfc || 'Sin RFC'}
                </span>
                {clienteData?.cliente_desc && (
                  <span>
                    <strong>Descripción:</strong> {clienteData.cliente_desc}
                  </span>
                )}
                {clienteData?.cliente_dv ? (
                  <span className="text-amber-700 font-medium bg-amber-50 px-1.5 py-0.5 rounded text-[10px]">
                    RFC repetido permitido
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-center">
            <Button
              variant={showEditMaster ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              className={showEditMaster ? 'bg-blue-600 text-white !text-xs !py-1 !px-3 !capitalize' : '!text-xs !py-1 !px-3 !capitalize'}
              startIcon={<Icon fontSize="small">{showEditMaster ? 'expand_less' : 'edit'}</Icon>}
              onClick={() => setShowEditMaster((prev) => !prev)}
            >
              {showEditMaster ? 'Ocultar Edición Base' : 'Editar Datos Base'}
            </Button>
          </div>
        </div>

        {/* Panel plegable para editar los datos base del cliente (carga diferida) */}
        <Collapse in={showEditMaster} unmountOnExit>
          {showEditMaster && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <ClienteForm
                id={id}
                isModal={true}
                onSave={() => {
                  ClienteService.invalidarDetalleCompleto(id);
                  fetchCliente();
                  setShowEditMaster(false);
                }}
                onCancel={() => setShowEditMaster(false)}
              />
            </div>
          )}
        </Collapse>
      </div>

      {/* 2. PANEL DE NAVEGACIÓN DE SUBSECCIONES ORGANIZADAS */}
      <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
        {/* Barra superior de Categorías y Búsqueda */}
        <div className="p-2.5 md:p-3 border-b border-gray-100 bg-gray-50/70">
          <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-2.5">
            {/* Píldoras de Categorías */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 lg:pb-0 scrollbar-none">
              {availableCategories.map((category) => {
                const count = accessibleSubsections.filter(
                  (s) => s.category === category.id
                ).length;
                const isCatActive = activeCategory === category.id;

                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setSearchTerm('');
                      // Si la subsección actual no pertenece a esta categoría, navegar a la primera de la nueva categoría
                      const catSubs = accessibleSubsections.filter(
                        (s) => s.category === category.id
                      );
                      if (
                        catSubs.length > 0 &&
                        (!currentSubsection || currentSubsection.category !== category.id)
                      ) {
                        history.push(`${baseUrl}/${catSubs[0].route}`);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium text-xs transition-all whitespace-nowrap shadow-2xs ${
                      isCatActive
                        ? 'bg-blue-600 text-white shadow-blue-500/20 font-bold'
                        : 'bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 border border-gray-200'
                    }`}
                  >
                    <Icon className="!text-base">{category.icon}</Icon>
                    <span>{category.name}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                        isCatActive ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Buscador Rápido de Subsecciones */}
            <div className="relative min-w-[220px]">
              <div className="flex items-center bg-white border border-gray-200 rounded-lg px-2.5 py-1 shadow-2xs focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                <Icon className="text-gray-400 mr-1.5 !text-xs">search</Icon>
                <input
                  type="text"
                  placeholder="Buscar entre las 17 subsecciones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-xs bg-transparent outline-none text-gray-800 placeholder-gray-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icon className="!text-xs">close</Icon>
                  </button>
                )}
              </div>

              {/* Menú flotante de resultados de búsqueda */}
              {searchTerm.trim().length > 0 && (
                <div className="absolute right-0 top-full mt-1.5 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-1.5 z-50 max-h-72 overflow-y-auto">
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-0.5">
                    Resultados ({searchResults.length})
                  </div>
                  {searchResults.length === 0 ? (
                    <div className="text-xs text-gray-500 p-2 text-center">
                      No se encontraron subsecciones
                    </div>
                  ) : (
                    searchResults.map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => handleSelectSubsection(sub)}
                        className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <Icon className="!text-sm text-gray-400">{sub.icon}</Icon>
                        <span className="font-medium flex-1">{sub.label}</span>
                        <span className="text-[10px] text-gray-400 capitalize">
                          {sub.category}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sub-pestañas de la categoría activa */}
          <div className="flex items-center gap-1.5 overflow-x-auto pt-2 mt-2 border-t border-gray-200/60 scrollbar-none">
            {categorySubsections.map((sub) => {
              const isSubActive = currentSubsection && currentSubsection.id === sub.id;

              return (
                <button
                  key={sub.id}
                  onClick={() => handleSelectSubsection(sub)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${
                    isSubActive
                      ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="!text-sm">{sub.icon}</Icon>
                  <span>{sub.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 3. CONTENEDOR DE RENDER DE LA SUBSECCIÓN SELECCIONADA */}
        <div className="p-3 md:p-4 min-h-[450px]">
          <Switch>
            {accessibleSubsections.map((sub) => {
              const SubComp = sub.Component;
              return (
                <Route
                  key={sub.route}
                  path={sub.path}
                  render={(props) => <SubComp {...props} back={false} reset={false} />}
                />
              );
            })}
            {accessibleSubsections[0] && (
              <Route
                key="default"
                exact
                path={baseUrl}
                render={(props) => {
                  const DefaultComp = accessibleSubsections[0].Component;
                  return <DefaultComp {...props} back={false} reset={false} />;
                }}
              />
            )}
          </Switch>
        </div>
      </div>
    </div>
  );
};

export default withRouter(ClienteInfo);
